// src/utils/ageCategory.ts
//
// Věková / pohlavní kategorie střelce v roce závodu – jedno pravidlo pro
// publikaci výsledků (publishResults) i veřejnou startovní listinu
// (startList, bezpečnost a: tabule a rozhodčí už nečtou rok narození z profilu).
// Přesunuto beze změny z publishResults.ts (tam se dál re-exportuje).

import type { DocumentData } from "firebase/firestore";
import {
  DEFAULT_CATEGORY_LIMITS,
  type CategoryDefaults,
  type ResultCategoryId,
} from "./categories";

/** Věková / pohlavní kategorie – nováček je přepínač navíc. */
export type AgeCategoryId = Exclude<ResultCategoryId, "novacci">;

/**
 * Horní věkové hranice pásem (věk ≤ hranice) – stejná pole defaults/categories
 * jako dosavadní publikace (junior.maxAge, man.maxAge, senior.maxAge),
 * null / chybějící hodnota → výchozí z categories.ts. Pro dokument uložený
 * editorem v AdminOverview (hlídá návaznost pásem) vychází totéž co
 * categoryLimitsOf: manMax + 1 = seniorMin, seniorMax + 1 = veteranMin.
 * Odvození z maxAge (ne categoryLimitsOf) drží text shodný s dosavadním
 * i pro ručně upravený nekonzistentní dokument.
 */
export interface PublishAgeLimits {
  juniorMax: number;
  manMax: number;
  seniorMax: number;
}

export function publishAgeLimitsOf(defaults: CategoryDefaults): PublishAgeLimits {
  return {
    juniorMax: defaults.junior.maxAge ?? DEFAULT_CATEGORY_LIMITS.juniorMax,
    manMax: defaults.man.maxAge ?? DEFAULT_CATEGORY_LIMITS.seniorMin - 1,
    seniorMax: defaults.senior.maxAge ?? DEFAULT_CATEGORY_LIMITS.veteranMin - 1,
  };
}

/**
 * Věková / pohlavní kategorie podle profilu – pravidlo 1:1 s dosavadní
 * publikací (i živou tabulí): junior (věk ≤ juniorMax) má přednost i u žen,
 * pak žena bez ohledu na věk, pak muži / senioři podle hranic, zbytek
 * veteráni – včetně neznámého roku narození (dosavadní chování, drží shodu
 * textu). Věk = rok závodu − rok narození.
 */
export function ageCategoryOf(
  profile: DocumentData,
  year: number,
  limits: PublishAgeLimits
): AgeCategoryId {
  // 1:1 s původním `u.birthYear ? year - u.birthYear : null` (Number() je totéž, co dělá minus)
  const birthYear: unknown = profile.birthYear;
  const age = birthYear ? year - Number(birthYear) : null;
  const gender = typeof profile.gender === "string" ? profile.gender.toUpperCase() : "";
  if (age !== null && age <= limits.juniorMax) return "juniori";
  if (gender === "ŽENA") return "zeny";
  if (age !== null && age <= limits.manMax) return "muzi";
  if (age !== null && age <= limits.seniorMax) return "seniori";
  return "veterani";
}
